import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { describePromoPrice } from '../store/cartSlice';
import { useGetProductsQuery, useGetCategoriesQuery, type ProductSort } from '../store/apiSlice';
import { AlertBanner, SizePicker, PriceRangeSlider, DrawerPanel, Button, ProductSkeletonGrid } from '../components/ui';
import { useCategories } from '../hooks/useCategories';
import {
  Search, SlidersHorizontal, ChevronRight, ChevronLeft, X,
} from 'lucide-react';

const PAGE_SIZE = 12;

function getPageRange(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | null)[] = [1];
  if (current > 3) pages.push(null);
  const lo = Math.max(2, current - 1);
  const hi = Math.min(total - 1, current + 1);
  for (let i = lo; i <= hi; i++) pages.push(i);
  if (current < total - 2) pages.push(null);
  pages.push(total);
  return pages;
}

export default function Catalogo() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('categoria');

  // UI states
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState({
    gender: true,
    sizes: true,
    price: true,
  });

  // Filter states (server-side)
  const [searchQuery, setSearchQuery] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedGender, setSelectedGender] = useState('Todos');
  const [sort, setSort] = useState<ProductSort>('default');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states (client-side)
  const [sizeFilters, setSizeFilters] = useState<string[]>([]);
  const [maxPrice, setMaxPrice] = useState(300000);

  const productAreaRef = useRef<HTMLDivElement>(null);

  const commitSearch = () => {
    setCommittedSearch(searchQuery);
    setCurrentPage(1);
    productAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // URL category param
  useEffect(() => {
    if (categoryParam) { setSelectedCategory(categoryParam); setCurrentPage(1); }
  }, [categoryParam]);

  // Categories (with icons for display)
  const { categories: backendCats } = useCategories();
  // Full API categories (with slug for server queries)
  const { data: apiCats = [] } = useGetCategoriesQuery();
  const categorySlugMap = Object.fromEntries(apiCats.map(c => [c.name, c.slug]));

  // Build server query params
  const genderParam = selectedGender === 'Hombre' ? 'hombre' : selectedGender === 'Mujer' ? 'mujer' : undefined;
  const categorySlug = selectedCategory !== 'Todos' ? (categorySlugMap[selectedCategory] ?? undefined) : undefined;

  const { data: productsData, isLoading: loading, isFetching, isError } = useGetProductsQuery({
    page: currentPage,
    limit: PAGE_SIZE,
    ...(committedSearch.trim() && { search: committedSearch.trim() }),
    ...(categorySlug && { categorySlug }),
    ...(genderParam && { gender: genderParam }),
    sort,
  });

  const pageProducts = productsData?.items ?? [];
  const totalProducts = productsData?.total ?? 0;
  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);

  // Client-side filters (size + price) within current page
  const filteredProducts = pageProducts.filter(p => {
    const productSizes = p.sizes ? p.sizes.split(', ').filter(Boolean) : [];
    const matchesSize = sizeFilters.length === 0 || sizeFilters.some(sf => productSizes.includes(sf));
    const matchesPrice = maxPrice >= 300000 || describePromoPrice(p).price <= maxPrice;
    return matchesSize && matchesPrice;
  });

  const categories = ['Todos', ...backendCats.map(c => c.name)];

  const toggleSizeFilter = (size: string) => {
    setSizeFilters(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
  };

  const toggleFilterSection = (section: 'gender' | 'sizes' | 'price') => {
    setExpandedFilters(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  const handleGenderChange = (gen: string) => {
    setSelectedGender(gen);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    productAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCommittedSearch('');
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setCommittedSearch('');
    setCurrentPage(1);
    setSelectedCategory('Todos');
    setSelectedGender('Todos');
    setSort('default');
    setSizeFilters([]);
    setMaxPrice(300000);
    setIsFilterDrawerOpen(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (committedSearch.trim()) count++;
    if (selectedCategory !== 'Todos') count++;
    if (selectedGender !== 'Todos') count++;
    if (sort !== 'default') count++;
    if (sizeFilters.length > 0) count++;
    if (maxPrice < 300000) count++;
    return count;
  };

  const error = isError ? 'Error al cargar productos' : null;

  return (
    <div className="flex flex-col min-h-screen bg-white pt-[81px]">
      <section id="catalog" className="flex-1 w-full bg-white h-[calc(100vh-81px)] overflow-hidden flex flex-col">

        {/* ── Header ── */}
        <div className="px-6 lg:px-16 py-3 border-b border-slate-200 flex items-center justify-between gap-3 shrink-0 bg-slate-50/50">
          <h3 className="font-heading font-bold text-base text-slate-900 whitespace-nowrap hidden sm:block">
            Nuestra Colección <span className="text-slate-400 font-normal text-xs">— Explorá y filtrá nuestros calzados y prendas</span>
          </h3>

          {/* Search bar */}
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && commitSearch()}
                placeholder="Buscar por nombre..."
                className="w-full bg-white border border-slate-200 focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/10 rounded-xl pl-10 pr-8 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={commitSearch}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-sm shadow-blue-500/20 transition-all flex items-center gap-1.5 whitespace-nowrap"
            >
              <Search className="w-3.5 h-3.5" />
              Buscar
            </button>
          </div>
        </div>

        {/* ── Split View ── */}
        <div className="flex-1 flex overflow-hidden w-full relative">

          {/* Left Sidebar — desktop only */}
          <aside className="hidden lg:block w-72 h-full border-r border-slate-200/80 p-6 overflow-y-auto shrink-0 space-y-5 bg-slate-50/30">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <h4 className="font-heading font-bold text-xs text-slate-900 flex items-center gap-2">
                <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-600" /> Filtros
              </h4>
              {getActiveFiltersCount() > 0 && (
                <button
                  onClick={resetFilters}
                  className="text-[9px] font-extrabold text-rose-600 hover:text-rose-500 transition-colors uppercase tracking-wider"
                >
                  Limpiar
                </button>
              )}
            </div>

            {/* Género */}
            <div className="space-y-2 border-b border-slate-200/60 pb-4">
              <button
                onClick={() => toggleFilterSection('gender')}
                className="w-full flex items-center justify-between text-[10px] text-slate-500 font-bold uppercase tracking-wider hover:text-slate-800 transition-colors focus:outline-none"
              >
                <span>Género</span>
                <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-300 ${expandedFilters.gender ? 'rotate-90' : ''}`} />
              </button>
              {expandedFilters.gender && (
                <div className="flex gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200/60 mt-2">
                  {['Todos', 'Hombre', 'Mujer'].map((gen) => (
                    <button
                      key={gen}
                      onClick={() => handleGenderChange(gen)}
                      className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all ${selectedGender === gen ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                        }`}
                    >
                      {gen}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Categorías */}
            <div className="space-y-2 border-b border-slate-200/60 pb-4">
              <span className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Categorías</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { handleCategoryChange(cat); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedCategory === cat ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-600'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Ordenar */}
            <div className="space-y-2 border-b border-slate-200/60 pb-4">
              <span className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Ordenar por</span>
              <div className="flex flex-col gap-1 mt-1">
                {([
                  ['default', 'Destacados'],
                  ['price_asc', 'Precio: menor a mayor'],
                  ['price_desc', 'Precio: mayor a menor'],
                  ['alpha', 'Nombre A–Z'],
                ] as [ProductSort, string][]).map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => { setSort(val); setCurrentPage(1); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${sort === val ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-600 border-transparent hover:bg-slate-100 hover:text-slate-900'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Talles */}
            <div className="space-y-2 border-b border-slate-200/60 pb-4">
              <button
                onClick={() => toggleFilterSection('sizes')}
                className="w-full flex items-center justify-between text-[10px] text-slate-500 font-bold uppercase tracking-wider hover:text-slate-800 transition-colors focus:outline-none"
              >
                <span>Talles / Tamaños</span>
                <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-300 ${expandedFilters.sizes ? 'rotate-90' : ''}`} />
              </button>
              {expandedFilters.sizes && (
                <div className="mt-2">
                  <SizePicker
                    sizes={['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '2', '3', '4', '5', 'S', 'M', 'L', 'XL', 'Único']}
                    selected={sizeFilters}
                    onSelect={toggleSizeFilter}
                    multi
                    variant="grid"
                  />
                </div>
              )}
            </div>

            {/* Precio */}
            <div className="space-y-2 pb-2">
              <button
                onClick={() => toggleFilterSection('price')}
                className="w-full flex items-center justify-between text-[10px] text-slate-500 font-bold uppercase tracking-wider hover:text-slate-800 transition-colors focus:outline-none"
              >
                <span>Rango de Precio</span>
                <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-300 ${expandedFilters.price ? 'rotate-90' : ''}`} />
              </button>
              {expandedFilters.price && (
                <div className="mt-2">
                  <PriceRangeSlider value={maxPrice} max={300000} onChange={setMaxPrice} />
                </div>
              )}
            </div>
          </aside>

          {/* Right Panel — scrollable + sticky pagination */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div ref={productAreaRef} className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 space-y-4 pb-4">
              {error && <AlertBanner message={error} variant="warning" />}

              {/* Count + mobile filter button */}
              <div className="flex justify-between items-center text-xs text-slate-650 bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-200 shadow-sm shrink-0">
                <span>
                  <strong className="text-slate-800">{totalProducts}</strong> productos encontrados
                  {(sizeFilters.length > 0 || maxPrice < 300000) && filteredProducts.length < pageProducts.length && (
                    <span className="text-slate-500 ml-1">· <strong className="text-slate-700">{filteredProducts.length}</strong> en esta página</span>
                  )}
                </span>
                <div className="flex gap-2">
                  <select
                    value={sort}
                    onChange={e => { setSort(e.target.value as ProductSort); setCurrentPage(1); }}
                    className="lg:hidden bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-700 font-semibold focus:outline-none focus:border-indigo-500/60 shadow-sm cursor-pointer"
                  >
                    <option value="default">Destacados</option>
                    <option value="price_asc">Precio ↑</option>
                    <option value="price_desc">Precio ↓</option>
                    <option value="alpha">A–Z</option>
                  </select>
                  <button
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="lg:hidden flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-all shadow-md shadow-blue-500/10"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" /> Filtrar
                    {getActiveFiltersCount() > 0 && (
                      <span className="ml-1 bg-white text-blue-600 font-extrabold text-[9px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center shadow-sm">
                        {getActiveFiltersCount()}
                      </span>
                    )}
                  </button>
                  {selectedCategory !== 'Todos' && (
                    <span className="hidden sm:inline-block bg-indigo-500/15 text-indigo-700 px-2 py-0.5 rounded border border-indigo-500/25 font-semibold text-[9px] uppercase tracking-wider">
                      {selectedCategory}
                    </span>
                  )}
                </div>
              </div>

              {loading ? (
                <ProductSkeletonGrid count={PAGE_SIZE} />
              ) : (
                <div className={isFetching ? 'opacity-60 pointer-events-none transition-opacity duration-150' : ''}>
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-20 bg-slate-50/50 rounded-2xl border border-slate-200 shadow-sm text-slate-650 space-y-4">
                      <SlidersHorizontal className="w-8 h-8 text-slate-400 mx-auto stroke-[1.5]" />
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-slate-850">Ningún producto coincide con tus filtros.</p>
                        <p className="text-xs text-slate-500">Probá ajustando la búsqueda o limpiando filtros.</p>
                      </div>
                      <Button variant="primary" onClick={resetFilters} className="text-xs">
                        Restablecer filtros
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-3">
                      {filteredProducts.map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Pagination bar (sticky bottom) ── */}
            {!loading && totalPages > 1 && (
              <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-3 flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-400 hidden sm:block">
                  Página {currentPage} de {totalPages}
                </span>

                <div className="flex items-center gap-1 mx-auto sm:mx-0">
                  <PaginationBtn
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </PaginationBtn>

                  {getPageRange(currentPage, totalPages).map((p, i) =>
                    p === null ? (
                      <span key={`e-${i}`} className="w-8 text-center text-xs text-slate-400 select-none">…</span>
                    ) : (
                      <PaginationBtn
                        key={p}
                        onClick={() => handlePageChange(p)}
                        disabled={false}
                        active={p === currentPage}
                      >
                        {p}
                      </PaginationBtn>
                    )
                  )}

                  <PaginationBtn
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </PaginationBtn>
                </div>

                <span className="text-[11px] text-slate-400 hidden sm:block">
                  {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalProducts)} de {totalProducts}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── MOBILE FILTER DRAWER ── */}
      <div className="lg:hidden">
        <DrawerPanel isOpen={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)} side="right">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
            <h3 className="font-heading font-bold text-sm text-slate-900 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-indigo-600" /> Filtrar Productos
            </h3>
            <button
              onClick={() => setIsFilterDrawerOpen(false)}
              className="text-slate-500 hover:text-slate-900 p-1.5 hover:bg-slate-200/60 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 p-6 overflow-y-auto space-y-6">
            <div className="space-y-2">
              <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Género</label>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200/65">
                {['Todos', 'Hombre', 'Mujer'].map((gen) => (
                  <button
                    key={gen}
                    onClick={() => { handleGenderChange(gen); setIsFilterDrawerOpen(false); }}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${selectedGender === gen ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                      }`}
                  >
                    {gen}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Categorías</label>
              <div className="flex flex-wrap gap-1.5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { handleCategoryChange(cat); setIsFilterDrawerOpen(false); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${selectedCategory === cat ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-400 hover:text-blue-600'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Talles / Tamaños</label>
              <SizePicker
                sizes={['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '2', '3', '4', '5', 'S', 'M', 'L', 'XL', 'Único']}
                selected={sizeFilters}
                onSelect={toggleSizeFilter}
                multi
                variant="grid"
              />
            </div>

            <PriceRangeSlider value={maxPrice} max={300000} onChange={setMaxPrice} />
          </div>

          <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3">
            <button
              onClick={resetFilters}
              className="flex-1 py-2.5 border border-slate-200 hover:bg-slate-100 text-slate-600 rounded-xl text-xs font-semibold transition-colors"
            >
              Limpiar
            </button>
            <button
              onClick={() => setIsFilterDrawerOpen(false)}
              className="flex-1 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-slate-955 hover:text-white border border-indigo-500 rounded-xl text-xs font-bold shadow-lg shadow-indigo-500/10 transition-colors"
            >
              Aplicar
            </button>
          </div>
        </DrawerPanel>
      </div>

    </div>
  );
}

// ── Sub-component ──────────────────────────────────────────────────────────────

const PaginationBtn: React.FC<{
  onClick: () => void;
  disabled: boolean;
  active?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, active, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-all border ${active
      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
      : disabled
        ? 'text-slate-300 border-transparent cursor-not-allowed'
        : 'text-slate-500 border-slate-200 hover:border-blue-500/40 hover:text-blue-600 hover:bg-blue-50'
      }`}
  >
    {children}
  </button>
);
